import ColumnMark from './ColumnMark';

/**
 * iAdvocate brand lockup — "The Column" treatment, matched to the corporate
 * suite's sidebar lockup (canonical: corporate-web/src/components/layout/
 * SidebarNav.tsx). The brass column reads as the "i" of iAdvocate beside the
 * mixed-case Marcellus wordmark, the pair standing on a brass plinth rule
 * closed by the single brass square.
 *
 * It used to render "ADVOCATE" all-caps with wide tracking and no plinth —
 * which broke the column-is-the-i illusion (tracked caps detach the mark from
 * the word). Keep this file visually in step with the corporate lockup; the
 * default metrics below are the corporate ones VERBATIM (38px mark, 27px
 * wordmark, 7px gap, 3px plinth) — a first scaled-down pass read as too
 * small next to the corporate suite.
 *
 * Marcellus ships weight 400 only — never add font-bold to the wordmark.
 */
interface LogoLockupProps {
  /** Show the mark only (e.g. collapsed sidebar). */
  collapsed?: boolean;
  className?: string;
  /** Override mark sizing/colour; defaults to brass on dark. */
  markClass?: string;
  /** Override wordmark sizing/colour. */
  textClass?: string;
}

export default function LogoLockup({
  collapsed = false,
  className = '',
  markClass = 'h-[38px] w-auto text-brass',
  textClass = 'font-display text-[27px] leading-none tracking-[0.01em] text-parchment',
}: LogoLockupProps) {
  if (collapsed) {
    return (
      <span className={`inline-flex ${className}`}>
        <ColumnMark className={markClass} />
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col ${className}`}>
      <span className="flex items-end gap-[7px]">
        <ColumnMark className={markClass} />
        <span className={textClass}>Advocate</span>
      </span>
      {/* Plinth rule — the wordmark stands on it, closed by the brass square. */}
      <span aria-hidden className="mt-[3px] flex items-center gap-[3px]">
        <span className="h-[3px] flex-1 bg-brass/60" />
        <span className="h-[3px] w-[3px] bg-brass" />
      </span>
    </span>
  );
}
